import { Effect, Schema } from 'effect';

import { requestCustomerMe } from '../../bind/customer';
import type { GtmPageType } from '@vs-shop/schemas/gtm/page-view';
import { GtmPageDataEventSchema, GtmPageViewEventSchema } from '@vs-shop/schemas/gtm/page-view';
import { hasAuthToken } from '../../utils/cookies/hasAuthToken';
import { sendGtmEvent } from '../../utils/gtm/gtm';

type PageViewData = {
  pageType: GtmPageType;
  breadcrumbs?: string | undefined;
};

export default function sendPageViewEvents(data: PageViewData) {
  return Effect.gen(function* () {
    const isLoggedIn = yield* hasAuthToken;

    // Send first event immediately
    yield* Schema.decodeEffect(GtmPageViewEventSchema)({
      event: 'track.page_view',
    }).pipe(Effect.flatMap(sendGtmEvent));

    const userData = yield* isLoggedIn
      ? requestCustomerMe.pipe(
          Effect.map((customer) => ({
            type: 'Customer' as const,
            id: customer.email,
            name: customer.fullName,
            email: customer.email,
            phone: customer.phoneNumber ?? undefined,
            address:
              customer.defaultAddress !== null
                ? {
                    city: customer.defaultAddress.city,
                    street: customer.defaultAddress.street,
                  }
                : undefined,
          })),
          Effect.orElseSucceed(() => ({
            type: 'Anonymous' as const,
          }))
        )
      : Effect.succeed({
          type: 'Anonymous' as const,
        });

    // Send second event with page and user data
    yield* Schema.decodeEffect(GtmPageDataEventSchema)({
      page: {
        type: data.pageType,
        breadcrumbs: data.breadcrumbs,
      },
      user: userData,
    }).pipe(Effect.flatMap(sendGtmEvent));
  });
}
